import React, { useState } from "react";
import { Group, Button, Menu, ActionIcon } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useDisclosure } from "@mantine/hooks";
import {
  IconDotsVertical,
  IconUserPlus,
  IconUser,
  IconCalendarClock,
  IconMailOff,
  IconUnlink,
} from "@tabler/icons-react";
import { useMessaging } from "../../context/MessagingContext";
import { useClient } from "../../context/ClientContext";
import ClientDetailsDrawer from "../clients/ClientDetailsDrawer";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import { AssignClientModal } from "./AssignClientModal";
import type { Conversation } from "../../types/messaging";
import type { Client } from "../../types/client";

interface ConversationHeaderActionsProps {
  conversation: Conversation;
  /** Open the per-conversation scheduled-messages panel. */
  onOpenScheduled: () => void;
}

/**
 * The conversation header's action cluster: link/reassign/detach a client, open
 * the client drawer, mark the thread unread, and reach the scheduled-messages
 * panel. Self-contained (reads MessagingContext + ClientContext) so the
 * ConversationView header stays declarative.
 */
export const ConversationHeaderActions: React.FC<
  ConversationHeaderActionsProps
> = ({ conversation, onOpenScheduled }) => {
  const { assignClient, setReadState } = useMessaging();
  // ClientContext is untyped JS — cast to the id→object map we need.
  const { clientsById } = useClient() as {
    clientsById: Record<string, Client>;
  };

  const [assignOpen, assign] = useDisclosure(false);
  const [drawerOpen, drawer] = useDisclosure(false);
  const [detachOpen, detach] = useDisclosure(false);
  const [detaching, setDetaching] = useState(false);

  const linked = conversation.client;
  const clientObject = linked ? (clientsById[linked.uuid] ?? null) : null;

  const handleDetach = async () => {
    setDetaching(true);
    try {
      await assignClient(conversation.conversation_id, null);
      detach.close();
    } catch {
      // Keep the confirmation open so the user can retry, and surface the failure.
      notifications.show({
        title: "Couldn't detach client",
        message: "Something went wrong. Please try again.",
        color: "var(--color-danger)",
      });
    } finally {
      setDetaching(false);
    }
  };

  const markUnread = () => setReadState(conversation.conversation_id, true);

  return (
    <>
      <Group gap="xs" wrap="nowrap">
        {linked ? (
          <Button
            variant="light"
            color="teal"
            size="xs"
            leftSection={<IconUser size={16} />}
            onClick={drawer.open}
          >
            View profile
          </Button>
        ) : (
          <Button
            variant="light"
            color="teal"
            size="xs"
            leftSection={<IconUserPlus size={16} />}
            onClick={assign.open}
          >
            Assign to client
          </Button>
        )}

        <Menu position="bottom-end" withArrow shadow="md">
          <Menu.Target>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="lg"
              aria-label="Conversation actions"
            >
              <IconDotsVertical size={18} />
            </ActionIcon>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconCalendarClock size={16} />}
              onClick={onOpenScheduled}
            >
              Scheduled messages
            </Menu.Item>
            <Menu.Item
              leftSection={<IconMailOff size={16} />}
              onClick={markUnread}
            >
              Mark as unread
            </Menu.Item>
            {linked && (
              <>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<IconUserPlus size={16} />}
                  onClick={assign.open}
                >
                  Reassign client
                </Menu.Item>
                <Menu.Item
                  color="red"
                  leftSection={<IconUnlink size={16} />}
                  onClick={detach.open}
                >
                  Detach client
                </Menu.Item>
              </>
            )}
          </Menu.Dropdown>
        </Menu>
      </Group>

      <AssignClientModal
        opened={assignOpen}
        onClose={assign.close}
        conversation={conversation}
      />

      <ClientDetailsDrawer
        client={clientObject}
        opened={drawerOpen}
        onClose={drawer.close}
        hideDeleteButton
      />

      <DeleteConfirmationModal
        isOpen={detachOpen}
        onClose={detach.close}
        onConfirm={handleDetach}
        isLoading={detaching}
        title="Detach client"
        message={
          "Detach this client from the conversation? The chat history stays, " +
          "but it will no longer be linked to the client."
        }
        confirmLabel="Detach"
      />
    </>
  );
};

export default ConversationHeaderActions;
